/**
 * KEIKKA Logistics — landing + two simple wizards.
 *
 * Three top-level routes mounted from this module:
 *   /logistics              → LogisticsLandingPage
 *   /logistics/post-load    → PostLoadPage (single-page wizard)
 *   /logistics/offer-capacity → OfferCapacityPage
 *
 * The page is intentionally light. It exists to hand a Norwegian
 * partner a working flow they can poke without auth.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Truck, Package, MapPin, ChevronRight, Loader2, CheckCircle2,
  Lock, Star, Share2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import GlobalNav from '@/components/GlobalNav';
import { useLanguage } from '@/lib/context';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = ['NO', 'SE', 'FI', 'EE'];
const CCY = { NO: 'NOK', SE: 'SEK', FI: 'EUR', EE: 'EUR' };
const CITIES = {
  NO: [
    { name: 'Oslo',      lat: 59.9139, lng: 10.7522 },
    { name: 'Bergen',    lat: 60.3913, lng:  5.3221 },
    { name: 'Trondheim', lat: 63.4305, lng: 10.3951 },
    { name: 'Stavanger', lat: 58.9700, lng:  5.7331 },
    { name: 'Drammen',   lat: 59.7440, lng: 10.2045 },
  ],
  SE: [
    { name: 'Stockholm', lat: 59.3293, lng: 18.0686 },
    { name: 'Göteborg',  lat: 57.7089, lng: 11.9746 },
    { name: 'Malmö',     lat: 55.6050, lng: 13.0038 },
    { name: 'Uppsala',   lat: 59.8586, lng: 17.6389 },
  ],
  FI: [
    { name: 'Helsinki',  lat: 60.1699, lng: 24.9384 },
    { name: 'Espoo',     lat: 60.2055, lng: 24.6559 },
    { name: 'Tampere',   lat: 61.4978, lng: 23.7610 },
    { name: 'Turku',     lat: 60.4518, lng: 22.2666 },
    { name: 'Oulu',      lat: 65.0121, lng: 25.4651 },
  ],
  EE: [
    { name: 'Tallinn',   lat: 59.4204, lng: 24.7470 },
    { name: 'Tartu',     lat: 58.3776, lng: 26.7290 },
    { name: 'Pärnu',     lat: 58.3859, lng: 24.4971 },
    { name: 'Narva',     lat: 59.3793, lng: 28.1791 },
  ],
};

const CARGO_TYPES = ['pallets', 'furniture', 'construction', 'machinery', 'documents', 'other'];
const VEHICLE_TYPES = ['van', 'box', 'box_truck', 'flatbed', 'curtain_sider', 'refrigerated',
                       'trailer', 'car_transporter', 'crane_hiab', 'pickup', 'other'];
const REQUIREMENTS = ['liftgate', 'fragile', 'adr', 'refrigerated', 'crane', 'tail_lift', 'straps'];

// Mobile-only sticky CTA. Renders a fixed bottom bar with the primary
// action (Postita veos / Publiser last / …) so it is always 1 tap away
// regardless of how far the user has scrolled. Hidden on ≥md screens
// because desktop already has the GlobalNav top bar.
const LogisticsMobileStickyCTA = () => {
  const { t } = useLanguage();
  return (
    <Link to="/logistics/post-load"
          data-testid="logistics-mobile-sticky-cta"
          className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm px-4 py-3 flex items-center justify-center gap-2 shadow-lg border-t border-orange-700">
      <Package className="h-4 w-4" />
      {t('logistics.actions.postLoad')}
    </Link>
  );
};

// ---------- Landing ----------
export const LogisticsLandingPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="logistics-landing">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-2">
            {t('logistics.title')}
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
          </h1>
          <p className="text-base text-muted-foreground">{t('logistics.subtitle')}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link to="/logistics/post-load" data-testid="logistics-cta-post-load">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600">
                <Package className="h-4 w-4 mr-1.5" />
                {t('logistics.actions.postLoad')}
              </Button>
            </Link>
            <Link to="/map?layer=logistics&sub=all" data-testid="logistics-cta-open-map">
              <Button size="sm" variant="outline">
                <MapPin className="h-4 w-4 mr-1.5" />
                {t('logistics.actions.openMap')}
              </Button>
            </Link>
          </div>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/logistics/post-load" data-testid="logistics-link-post-load"
                className="block rounded-2xl border bg-white p-6 hover:border-slate-900 hover:shadow-sm transition">
            <Package className="h-7 w-7 text-slate-900" />
            <h2 className="mt-3 text-lg font-semibold">{t('logistics.cards.postLoad.title')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('logistics.cards.postLoad.desc')}</p>
          </Link>
          <Link to="/logistics/find-carrier" data-testid="logistics-link-find-carrier"
                className="block rounded-2xl border bg-white p-6 hover:border-slate-900 hover:shadow-sm transition">
            <MapPin className="h-7 w-7 text-slate-900" />
            <h2 className="mt-3 text-lg font-semibold">{t('logistics.cards.findCarrier.title')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('logistics.cards.findCarrier.desc')}</p>
          </Link>
          <Link to="/logistics/backhaul" data-testid="logistics-link-backhaul"
                className="block rounded-2xl border bg-white p-6 hover:border-slate-900 hover:shadow-sm transition">
            <Truck className="h-7 w-7 text-slate-900" />
            <h2 className="mt-3 text-lg font-semibold">{t('logistics.cards.backhaul.title')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('logistics.cards.backhaul.desc')}</p>
          </Link>
        </div>
        <Link to="/map?layer=logistics&sub=all"
              className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
              data-testid="logistics-view-map">
          <MapPin className="h-4 w-4" /> {t('logistics.viewMap')}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      <LogisticsMobileStickyCTA />
    </div>
  );
};

// ---------- Helpers shared by both wizards ----------
const usePersistedCountry = () => useState('NO');

const CountryCitySelect = ({ country, city, onCountry, onCity, idPrefix }) => {
  const { t } = useLanguage();
  const cities = CITIES[country] || [];
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">{t('logistics.form.country')}</Label>
        <Select value={country} onValueChange={onCountry}>
          <SelectTrigger data-testid={`${idPrefix}-country`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {COUNTRIES.map(c => (
              <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">{t('logistics.form.city')}</Label>
        <Select value={city} onValueChange={onCity}>
          <SelectTrigger data-testid={`${idPrefix}-city`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {cities.map(c => (
              <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const cityCoords = (country, name) => {
  const list = CITIES[country] || [];
  return list.find(c => c.name === name) || list[0];
};

// ---------- Post Load wizard ----------
export const PostLoadPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [countryFrom, setCountryFrom] = useState('NO');
  const [cityFrom, setCityFrom] = useState('Oslo');
  const [countryTo, setCountryTo] = useState('NO');
  const [cityTo, setCityTo] = useState('Bergen');
  const [cargoType, setCargoType] = useState('pallets');
  const [weight, setWeight] = useState('');
  const [volume, setVolume] = useState('');
  const [pallets, setPallets] = useState('');
  const [pickupStart, setPickupStart] = useState('');
  const [deliveryStart, setDeliveryStart] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);

  const ccy = CCY[countryFrom] || 'EUR';

  const toggleReq = (r) => {
    setRequirements(rs => rs.includes(r) ? rs.filter(x => x !== r) : [...rs, r]);
  };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const f = cityCoords(countryFrom, cityFrom);
      const t2 = cityCoords(countryTo, cityTo);
      const payload = {
        country_from: countryFrom, city_from: cityFrom, lat_from: f.lat, lng_from: f.lng,
        country_to: countryTo,     city_to: cityTo,     lat_to: t2.lat, lng_to: t2.lng,
        pickup_window_start: pickupStart || null,
        delivery_window_start: deliveryStart || null,
        cargo_type: cargoType,
        cargo_weight_kg: weight ? parseFloat(weight) : null,
        cargo_volume_m3: volume ? parseFloat(volume) : null,
        pallets: pallets ? parseInt(pallets, 10) : null,
        requirements,
        budget: {
          mode: 'range',
          min: budgetMin ? parseFloat(budgetMin) : 0,
          max: budgetMax ? parseFloat(budgetMax) : 0,
          currency: ccy,
        },
        notes: notes || null,
        is_demo: true,
      };
      const r = await axios.post(`${API}/api/logistics/loads`, payload);
      setResult(r.data);
      // Auto-match
      const m = await axios.post(`${API}/api/logistics/match`, { load_id: r.data.id });
      setMatches(m.data.matches || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <GlobalNav />
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="logistics-post-load-success">
          <div className="rounded-2xl border bg-white p-6 space-y-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <h1 className="text-xl font-semibold">{t('logistics.success.load')}</h1>
              <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
            </div>

            <h2 className="text-base font-semibold pt-2 border-t">
              {t('logistics.matchedHeader', { count: matches.length })}
            </h2>
            {matches.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('logistics.empty')}
              </div>
            ) : (
              <ul className="divide-y" data-testid="logistics-matches-list">
                {matches.map(m => (
                  <li key={m.id} className="py-3" data-testid={`logistics-match-${m.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-medium">{m.carrier_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <MapPin className="h-3 w-3" />{m.start_city} → {m.end_city}
                          {m.carrier_rating && (
                            <span className="flex items-center gap-0.5">
                              <Star className="h-3 w-3 text-amber-500" />{m.carrier_rating}
                            </span>
                          )}
                          <Badge variant="secondary" className="text-[10px]">{t(`logistics.veh.${m.vehicle_type}`)}</Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" data-testid={`logistics-unlock-${m.id}`}
                              onClick={() => axios.post(`${API}/api/logistics/unlock`, { item_id: m.id, item_kind: 'capacity' })}>
                        <Lock className="h-3.5 w-3.5 mr-1" />{t('logistics.unlockContact')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Button onClick={() => navigate(`/logistics/find-carrier?country=${countryFrom}&city=${encodeURIComponent(cityFrom)}`)}
                    data-testid="logistics-success-map-cta">
              <MapPin className="h-4 w-4 mr-1" />{t('logistics.findCarriers')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="logistics-post-load">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {t('logistics.cards.postLoad.title')}
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">{t('logistics.subtitle')}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to={`/map?mode=logistics&originCountry=${countryFrom}&originCity=${encodeURIComponent(cityFrom)}&destCountry=${countryTo}&destCity=${encodeURIComponent(cityTo)}`}
              data-testid="logistics-show-route"
            >
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                <MapPin className="h-4 w-4 mr-1.5" />
                {t('logistics.actions.showRoute')}
              </Button>
            </Link>
            <Link
              to={`/map?layer=logistics&sub=all&country=${countryFrom}`}
              data-testid="logistics-open-map"
            >
              <Button size="sm" variant="outline">
                <MapPin className="h-4 w-4 mr-1.5" />
                {t('logistics.actions.openMap')}
              </Button>
            </Link>
          </div>
        </header>

        <section className="rounded-xl border bg-white p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-medium">{t('logistics.form.from')}</Label>
              <CountryCitySelect country={countryFrom} city={cityFrom}
                                 onCountry={(c) => { setCountryFrom(c); setCityFrom(CITIES[c][0].name); }}
                                 onCity={setCityFrom} idPrefix="logistics-from" />
            </div>
            <div>
              <Label className="font-medium">{t('logistics.form.to')}</Label>
              <CountryCitySelect country={countryTo} city={cityTo}
                                 onCountry={(c) => { setCountryTo(c); setCityTo(CITIES[c][0].name); }}
                                 onCity={setCityTo} idPrefix="logistics-to" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">{t('logistics.form.cargoType')}</Label>
              <Select value={cargoType} onValueChange={setCargoType}>
                <SelectTrigger data-testid="logistics-cargo-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARGO_TYPES.map(c => <SelectItem key={c} value={c}>{t(`logistics.cargo.${c}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{t('logistics.form.weightKg')}</Label>
              <Input type="number" min={0} value={weight} onChange={e => setWeight(e.target.value)} data-testid="logistics-weight" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.volumeM3')}</Label>
              <Input type="number" min={0} value={volume} onChange={e => setVolume(e.target.value)} data-testid="logistics-volume" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.pallets')}</Label>
              <Input type="number" min={0} value={pallets} onChange={e => setPallets(e.target.value)} data-testid="logistics-pallets" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.pickupStart')}</Label>
              <Input type="datetime-local" value={pickupStart} onChange={e => setPickupStart(e.target.value)} data-testid="logistics-pickup-start" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.deliveryStart')}</Label>
              <Input type="datetime-local" value={deliveryStart} onChange={e => setDeliveryStart(e.target.value)} data-testid="logistics-delivery-start" />
            </div>
          </div>

          <div>
            <Label className="text-xs">{t('logistics.form.requirements')}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {REQUIREMENTS.map(r => (
                <button key={r} type="button"
                        onClick={() => toggleReq(r)}
                        data-testid={`logistics-req-${r}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${
                          requirements.includes(r)
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-slate-300 hover:bg-slate-50'
                        }`}>
                  {t(`logistics.req.${r}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t('logistics.form.budget')} ({ccy}) — min</Label>
              <Input type="number" min={0} value={budgetMin} onChange={e => setBudgetMin(e.target.value)} data-testid="logistics-budget-min" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.budget')} ({ccy}) — max</Label>
              <Input type="number" min={0} value={budgetMax} onChange={e => setBudgetMax(e.target.value)} data-testid="logistics-budget-max" />
            </div>
          </div>

          <Textarea placeholder={t('logistics.form.notes')} value={notes}
                    onChange={e => setNotes(e.target.value)} rows={2} data-testid="logistics-notes" />

          {error && (
            <div className="text-sm text-rose-600 border border-rose-200 bg-rose-50 rounded p-2">
              <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1" />{String(error)}
            </div>
          )}

          <Button onClick={submit} disabled={submitting} className="w-full" data-testid="logistics-submit-load">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('logistics.submitting')}</> : t('logistics.submit.load')}
          </Button>
        </section>
      </div>
    </div>
  );
};

// ---------- Offer Capacity wizard ----------
export const OfferCapacityPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [carrierName, setCarrierName] = useState('');
  const [startCountry, setStartCountry] = useState('NO');
  const [startCity, setStartCity] = useState('Oslo');
  const [endCountry, setEndCountry] = useState('NO');
  const [endCity, setEndCity] = useState('Bergen');
  const [vehicleType, setVehicleType] = useState('box');
  const [capKg, setCapKg] = useState('');
  const [capM3, setCapM3] = useState('');
  const [palletSpaces, setPalletSpaces] = useState('');
  const [priceMode, setPriceMode] = useState('per_km');
  const [priceVal, setPriceVal] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableTo, setAvailableTo] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const ccy = CCY[startCountry] || 'EUR';

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const f = cityCoords(startCountry, startCity);
      const t2 = cityCoords(endCountry, endCity);
      const payload = {
        carrier_name: carrierName || `Anonymous ${startCountry}`,
        start_country: startCountry, start_city: startCity, start_lat: f.lat, start_lng: f.lng,
        end_country: endCountry, end_city: endCity, end_lat: t2.lat, end_lng: t2.lng,
        available_from: availableFrom || null,
        available_to: availableTo || null,
        vehicle_type: vehicleType,
        capacity_kg: capKg ? parseFloat(capKg) : null,
        capacity_m3: capM3 ? parseFloat(capM3) : null,
        pallet_spaces: palletSpaces ? parseInt(palletSpaces, 10) : null,
        price: priceVal ? { mode: priceMode, value: parseFloat(priceVal), currency: ccy } : null,
        notes: notes || null,
        is_demo: true,
      };
      const r = await axios.post(`${API}/api/logistics/capacity`, payload);
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <GlobalNav />
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="logistics-offer-capacity-success">
          <div className="rounded-2xl border bg-white p-6 space-y-4">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <h1 className="text-xl font-semibold">{t('logistics.success.capacity')}</h1>
              <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
            </div>
            <Button onClick={() => navigate(`/map?layer=logistics&sub=capacity&country=${startCountry}`)}
                    data-testid="logistics-capacity-map-cta">
              <MapPin className="h-4 w-4 mr-1" />{t('logistics.viewListing')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="logistics-offer-capacity">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {t('logistics.cards.offerCapacity.title')}
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">{t('logistics.subtitle')}</p>
        </header>

        <section className="rounded-xl border bg-white p-4 space-y-4">
          <Input placeholder={t('logistics.form.carrierName')} value={carrierName}
                 onChange={e => setCarrierName(e.target.value)} data-testid="logistics-carrier-name" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-medium">{t('logistics.form.from')}</Label>
              <CountryCitySelect country={startCountry} city={startCity}
                                 onCountry={(c) => { setStartCountry(c); setStartCity(CITIES[c][0].name); }}
                                 onCity={setStartCity} idPrefix="logistics-cap-from" />
            </div>
            <div>
              <Label className="font-medium">{t('logistics.form.to')}</Label>
              <CountryCitySelect country={endCountry} city={endCity}
                                 onCountry={(c) => { setEndCountry(c); setEndCity(CITIES[c][0].name); }}
                                 onCity={setEndCity} idPrefix="logistics-cap-to" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">{t('logistics.form.vehicleType')}</Label>
              <Select value={vehicleType} onValueChange={setVehicleType}>
                <SelectTrigger data-testid="logistics-cap-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(v => <SelectItem key={v} value={v}>{t(`logistics.veh.${v}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{t('logistics.form.capacityKg')}</Label>
              <Input type="number" min={0} value={capKg} onChange={e => setCapKg(e.target.value)} data-testid="logistics-cap-kg" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.capacityM3')}</Label>
              <Input type="number" min={0} value={capM3} onChange={e => setCapM3(e.target.value)} data-testid="logistics-cap-m3" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.palletSpaces')}</Label>
              <Input type="number" min={0} value={palletSpaces} onChange={e => setPalletSpaces(e.target.value)} data-testid="logistics-cap-pallets" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.availableFrom')}</Label>
              <Input type="datetime-local" value={availableFrom} onChange={e => setAvailableFrom(e.target.value)} data-testid="logistics-cap-from-time" />
            </div>
            <div><Label className="text-xs">{t('logistics.form.availableTo')}</Label>
              <Input type="datetime-local" value={availableTo} onChange={e => setAvailableTo(e.target.value)} data-testid="logistics-cap-to-time" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('logistics.form.priceMode')}</Label>
              <Select value={priceMode} onValueChange={setPriceMode}>
                <SelectTrigger data-testid="logistics-cap-price-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_km">{t('logistics.form.price.per_km')}</SelectItem>
                  <SelectItem value="fixed_min">{t('logistics.form.price.fixed_min')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">{ccy}</Label>
              <Input type="number" min={0} value={priceVal} onChange={e => setPriceVal(e.target.value)} data-testid="logistics-cap-price-value" />
            </div>
          </div>

          <Textarea placeholder={t('logistics.form.notes')} value={notes}
                    onChange={e => setNotes(e.target.value)} rows={2} data-testid="logistics-cap-notes" />

          {error && (
            <div className="text-sm text-rose-600 border border-rose-200 bg-rose-50 rounded p-2">
              {String(error)}
            </div>
          )}

          <Button onClick={submit} disabled={submitting} className="w-full" data-testid="logistics-submit-capacity">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('logistics.submitting')}</> : t('logistics.submit.capacity')}
          </Button>
        </section>
      </div>
    </div>
  );
};

// ---------- Find Carrier (filters + list + open-on-map) ----------
export const FindCarrierPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [country, setCountry] = useState(searchParams.get('country') || 'NO');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [vehicle, setVehicle] = useState(searchParams.get('vehicleType') || '');
  const [minKg, setMinKg] = useState('');
  const [sort, setSort] = useState('rating');
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    if (city) params.set('city', city);
    if (vehicle) params.set('vehicle_type', vehicle);
    if (minKg) params.set('min_capacity_kg', minKg);
    if (sort) params.set('sort', sort);
    axios.get(`${API}/api/logistics/carriers?${params.toString()}`)
      .then(r => { if (active) setCarriers(r.data.items || []); })
      .catch(() => { if (active) setCarriers([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [country, city, vehicle, minKg, sort]);

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="logistics-find-carrier">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {t('logistics.findCarrier.title')}
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">{t('logistics.findCarrier.subtitle')}</p>
        </header>

        <section className="rounded-xl border bg-white p-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <Label className="text-xs">{t('logistics.findCarrier.filter.country')}</Label>
              <Select value={country} onValueChange={(c) => { setCountry(c); setCity(''); }}>
                <SelectTrigger data-testid="find-carrier-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.findCarrier.filter.city')}</Label>
              <Select value={city || '__any'} onValueChange={(v) => setCity(v === '__any' ? '' : v)}>
                <SelectTrigger data-testid="find-carrier-city"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">—</SelectItem>
                  {(CITIES[country] || []).map(c => (
                    <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.findCarrier.filter.vehicle')}</Label>
              <Select value={vehicle || '__any'} onValueChange={(v) => setVehicle(v === '__any' ? '' : v)}>
                <SelectTrigger data-testid="find-carrier-vehicle"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">—</SelectItem>
                  {VEHICLE_TYPES.map(v => (
                    <SelectItem key={v} value={v}>{t(`logistics.veh.${v}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.findCarrier.filter.minKg')}</Label>
              <Input type="number" min={0} value={minKg} onChange={e => setMinKg(e.target.value)}
                     data-testid="find-carrier-min-kg" />
            </div>
            <div>
              <Label className="text-xs">&nbsp;</Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="find-carrier-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">{t('logistics.findCarrier.sort.rating')}</SelectItem>
                  <SelectItem value="nearest">{t('logistics.findCarrier.sort.nearest')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="rounded-xl border bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-medium" data-testid="find-carrier-results-header">
              {t('logistics.findCarrier.results', { count: carriers.length })}
            </h2>
            <Button variant="outline" size="sm"
                    onClick={() => navigate(`/map?layer=logistics&sub=capacity&country=${country}`)}
                    data-testid="find-carrier-open-map">
              <MapPin className="h-3.5 w-3.5 mr-1" />{t('logistics.findCarrier.openMap')}
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />…
            </div>
          ) : carriers.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground"
                 data-testid="find-carrier-empty">
              {t('logistics.findCarrier.empty')}
            </div>
          ) : (
            <ul className="divide-y" data-testid="find-carrier-list">
              {carriers.map(c => (
                <li key={c.id} className="py-3" data-testid={`find-carrier-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {c.carrier_name}
                        {c.verified && <Badge variant="secondary" className="text-[10px]">✓</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                        <MapPin className="h-3 w-3" />{c.start_city} → {c.end_city}
                        {c.carrier_rating && (
                          <span className="flex items-center gap-0.5">
                            <Star className="h-3 w-3 text-amber-500" />{c.carrier_rating}
                          </span>
                        )}
                        <Badge variant="secondary" className="text-[10px]">
                          {t(`logistics.veh.${c.vehicle_type}`, { defaultValue: c.vehicle_type })}
                        </Badge>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`find-carrier-unlock-${c.id}`}
                            onClick={() => axios.post(`${API}/api/logistics/unlock`, { item_id: c.id, item_kind: 'capacity' })}>
                      <Lock className="h-3.5 w-3.5 mr-1" />{t('logistics.unlockContact')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <LogisticsMobileStickyCTA />
    </div>
  );
};

// ---------- Backhaul (route A→B + suggested matches) ----------
export const BackhaulPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [country, setCountry] = useState('NO');
  const [cityFrom, setCityFrom] = useState('Oslo');
  const [cityTo, setCityTo] = useState('Bergen');
  const [vehicle, setVehicle] = useState('box');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [matches, setMatches] = useState([]);

  const findMatches = async () => {
    try {
      const r = await axios.get(`${API}/api/logistics/carriers`, {
        params: { country, city: cityFrom, vehicle_type: vehicle, sort: 'nearest' },
      });
      setMatches(r.data.items || []);
      setSubmitted(true);
    } catch {
      setMatches([]); setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="logistics-backhaul">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {t('logistics.backhaul.title')}
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">DEMO</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">{t('logistics.backhaul.subtitle')}</p>
        </header>

        <section className="rounded-xl border bg-white p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('logistics.form.country')}</Label>
              <Select value={country} onValueChange={(c) => { setCountry(c); setCityFrom(CITIES[c][0].name); setCityTo(CITIES[c][1]?.name || CITIES[c][0].name); }}>
                <SelectTrigger data-testid="backhaul-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.form.vehicleType')}</Label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger data-testid="backhaul-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(v => (
                    <SelectItem key={v} value={v}>{t(`logistics.veh.${v}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.form.from')}</Label>
              <Select value={cityFrom} onValueChange={setCityFrom}>
                <SelectTrigger data-testid="backhaul-from"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(CITIES[country] || []).map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.form.to')}</Label>
              <Select value={cityTo} onValueChange={setCityTo}>
                <SelectTrigger data-testid="backhaul-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(CITIES[country] || []).map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('logistics.form.availableFrom')}</Label>
              <Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
                     data-testid="backhaul-date" />
            </div>
          </div>

          <Textarea placeholder={t('logistics.form.notes')} value={notes}
                    onChange={e => setNotes(e.target.value)} rows={2} data-testid="backhaul-notes" />

          <Button onClick={findMatches} className="w-full" data-testid="backhaul-submit">
            {t('logistics.findCarriers')}
          </Button>
        </section>

        {submitted && (
          <section className="rounded-xl border bg-white p-4 space-y-3" data-testid="backhaul-suggested">
            <h2 className="font-medium">
              {t('logistics.backhaul.suggestedHeader', { count: matches.length })}
            </h2>
            {matches.length === 0 ? (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('logistics.empty')}
              </div>
            ) : (
              <ul className="divide-y">
                {matches.slice(0, 5).map(m => (
                  <li key={m.id} className="py-3" data-testid={`backhaul-match-${m.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-medium">{m.carrier_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <MapPin className="h-3 w-3" />{m.start_city} → {m.end_city}
                          {m.carrier_rating && (
                            <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-500" />{m.carrier_rating}</span>
                          )}
                          <Badge variant="secondary" className="text-[10px]">
                            {t(`logistics.veh.${m.vehicle_type}`, { defaultValue: m.vehicle_type })}
                          </Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="outline"
                              onClick={() => axios.post(`${API}/api/logistics/unlock`, { item_id: m.id, item_kind: 'capacity' })}>
                        <Lock className="h-3.5 w-3.5 mr-1" />{t('logistics.unlockContact')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
      <LogisticsMobileStickyCTA />
    </div>
  );
};

export default LogisticsLandingPage;
